import emailjs from "@emailjs/browser";
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileText, Loader2, Upload, X } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";

const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ACCEPTED_EXT = [".pdf", ".doc", ".docx"];

function isAllowedFile(file) {
  if (!file) return false;
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  const lower = file.name.toLowerCase();
  return ACCEPTED_EXT.some((ext) => lower.endsWith(ext));
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ApplicationForm({ roles, selectedRole = "", onRoleConsumed }) {
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const [role, setRole] = useState(selectedRole);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedRole) {
      setRole(selectedRole);
      onRoleConsumed?.();
    }
  }, [selectedRole, onRoleConsumed]);

  const clearResume = () => {
    setResume(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setError("");
    if (!file) {
      setResume(null);
      return;
    }
    if (!isAllowedFile(file)) {
      setError("Please upload a PDF, DOC, or DOCX resume.");
      clearResume();
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError(`Resume must be under ${formatSize(MAX_FILE_BYTES)}. Your file is ${formatSize(file.size)}.`);
      clearResume();
      return;
    }
    setResume(file);
  };

  const resetForm = () => {
    setSubmitted(false);
    setError("");
    setRole("");
    clearResume();
    formRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!resume) {
      setError("Please upload your resume before submitting.");
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId =
      process.env.REACT_APP_EMAILJS_CAREERS_TEMPLATE_ID || process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey =
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY || process.env.REACT_APP_EMAILJS_USER_ID;

    if (!serviceId || !templateId || !publicKey) {
      setError(
        "Application form is not configured yet. Please email your resume to support@quantumsparktechnology.com."
      );
      return;
    }

    setSubmitting(true);
    try {
      await emailjs.sendForm(serviceId, templateId, formRef.current, { publicKey });
      setSubmitted(true);
      clearResume();
      formRef.current?.reset();
      setRole("");
    } catch (err) {
      console.error(err);
      setError(
        "Could not submit your application. Please try again, or email your resume to support@quantumsparktechnology.com."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-3xl border border-zinc-200 bg-white p-8 text-center shadow-sm md:p-12">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
          <CheckCircle2 size={36} />
        </div>
        <h3 className="mb-3 font-display text-2xl font-bold text-brand-ink">Application received</h3>
        <p className="mb-8 text-brand-muted">
          Thanks for applying to Quantum Spark Technology. Our team will review your resume and reach out if there is a
          potential fit.
        </p>
        <Button variant="outline" className="rounded-full px-6" onClick={resetForm}>
          Submit another application
        </Button>
      </div>
    );
  }

  return (
    <form
      id="apply"
      ref={formRef}
      onSubmit={handleSubmit}
      className="scroll-mt-28 space-y-6 rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm md:p-10"
      encType="multipart/form-data"
    >
      <div>
        <h2 className="mb-2 font-display text-2xl font-bold text-brand-ink">Apply online</h2>
        <p className="text-sm leading-relaxed text-brand-muted">
          Fill out the form and upload your resume. Applications go to{" "}
          <a href="mailto:support@quantumsparktechnology.com" className="font-medium text-brand-purple hover:underline">
            support@quantumsparktechnology.com
          </a>
          .
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-ink" htmlFor="applicant_name">
            Full name <span className="text-red-500">*</span>
          </label>
          <Input id="applicant_name" name="applicant_name" required placeholder="Your full name" className="bg-zinc-50" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-ink" htmlFor="applicant_email">
            Email <span className="text-red-500">*</span>
          </label>
          <Input
            id="applicant_email"
            name="applicant_email"
            type="email"
            required
            placeholder="you@email.com"
            className="bg-zinc-50"
          />
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-ink" htmlFor="applicant_phone">
            Phone
          </label>
          <Input id="applicant_phone" name="applicant_phone" type="tel" placeholder="+1 / +92 …" className="bg-zinc-50" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-ink" htmlFor="role">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            id="role"
            name="role"
            required
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="flex h-10 w-full rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/30"
          >
            <option value="">Select a role</option>
            {roles.map((r) => (
              <option key={r.title} value={r.title}>
                {r.title}
              </option>
            ))}
            <option value="General Application">General Application</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-brand-ink" htmlFor="portfolio_url">
          LinkedIn / Portfolio / GitHub
        </label>
        <Input
          id="portfolio_url"
          name="portfolio_url"
          type="url"
          placeholder="https://linkedin.com/in/you or https://github.com/you"
          className="bg-zinc-50"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-brand-ink" htmlFor="cover_letter">
          Short note <span className="text-red-500">*</span>
        </label>
        <textarea
          id="cover_letter"
          name="cover_letter"
          required
          rows={4}
          placeholder="Tell us why you want to join, relevant experience, and when you can start…"
          className="flex w-full rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-brand-ink placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/30"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-brand-ink" htmlFor="resume">
          Resume / CV <span className="text-red-500">*</span>
        </label>
        <input
          ref={fileInputRef}
          id="resume"
          name="resume"
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="sr-only"
          onChange={handleFileChange}
        />
        {!resume ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 px-6 py-10 text-center transition-colors hover:border-brand-purple/40 hover:bg-violet-50/40"
          >
            <Upload className="text-brand-purple" size={28} />
            <span className="text-sm font-semibold text-brand-ink">Click to upload resume</span>
            <span className="text-xs text-brand-muted">PDF, DOC, or DOCX · max {formatSize(MAX_FILE_BYTES)}</span>
          </button>
        ) : (
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-brand-purple">
                <FileText size={20} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-brand-ink">{resume.name}</p>
                <p className="text-xs text-brand-muted">{formatSize(resume.size)}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={clearResume}
              className="rounded-full p-2 text-zinc-500 hover:bg-zinc-200 hover:text-brand-ink"
              aria-label="Remove resume"
            >
              <X size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Hidden field for EmailJS template routing / subject clarity */}
      <input type="hidden" name="form_type" value="Career Application" />
      <input type="hidden" name="to_email" value="support@quantumsparktechnology.com" />

      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={submitting}
        className="h-12 w-full rounded-full bg-brand-purple text-white hover:bg-brand-purple-dark sm:w-auto sm:px-10"
      >
        {submitting ? (
          <>
            <Loader2 className="animate-spin" size={18} /> Submitting…
          </>
        ) : (
          "Submit application"
        )}
      </Button>

      <p className="text-xs leading-relaxed text-brand-muted">
        By submitting, you agree we may contact you about this application. Prefer email? Send your resume to{" "}
        <a href="mailto:support@quantumsparktechnology.com" className="text-brand-purple hover:underline">
          support@quantumsparktechnology.com
        </a>
        .
      </p>
    </form>
  );
}
